<template>
  <div class="upload-slip-modal">
    <h4 class="modal-title">
      <span class="title-icon-wrap"><i class="fal fa-upload"></i></span>
      {{ $t("codTransferWaiting.upload_transfer_slip") }}
    </h4>

    <div class="summary-row">
      <span class="summary-label">{{ $t("codTransferWaiting.shop") }}</span>
      <span class="summary-value strong">{{ item.name }}</span>
    </div>
    <div class="summary-row">
      <span class="summary-label">{{ $t("codTransferWaiting.bill_count") }}</span>
      <span class="summary-value strong amount">{{ formatCurrency(item.bill_count) }} {{ $t("common.ກີບ") }}</span>
    </div>

    <div class="form-group">
      <label class="label">
        {{ $t("codTransferWaiting.transfer_slip_image") }} <span class="text-danger">*</span>
      </label>
      <label
        class="dropzone"
        :class="{ 'has-file': !!previewUrl }"
        for="slip-file-input"
      >
        <img v-if="previewUrl" :src="previewUrl" class="preview-img" alt="slip preview" />
        <div v-else class="dropzone-empty">
          <div class="dropzone-icon"><i class="fal fa-upload"></i></div>
          <div class="dropzone-text">
            {{ $t("codTransferWaiting.drop_image_here_or") }}
            <span class="link">{{ $t("codTransferWaiting.choose_file") }}</span>
          </div>
          <div class="dropzone-sub">{{ $t("codTransferWaiting.file_hint") }}</div>
        </div>
      </label>
      <input
        id="slip-file-input"
        type="file"
        accept="image/png,image/jpeg"
        class="file-hidden"
        @change="onFileChange"
      />
      <p class="error-text" v-if="errors.slip">{{ errors.slip }}</p>
    </div>

    <div class="form-group">
      <label class="label">{{ $t("codTransferWaiting.bill_count") }} <span class="text-danger">*</span></label>
      <div class="input-with-suffix">
        <input class="input" type="number" min="0" v-model="form.bill_count" placeholder="0" />
        <span class="suffix">{{ $t("common.ກີບ") }}</span>
      </div>
      <p class="error-text" v-if="errors.bill_count">{{ errors.bill_count }}</p>
    </div>

    <div class="form-group">
      <label class="label">{{ $t("codTransferWaiting.transfer_date") }} <span class="text-danger">*</span></label>
      <input class="input" type="date" v-model="form.transfer_date" />
      <p class="error-text" v-if="errors.transfer_date">{{ errors.transfer_date }}</p>
    </div>

    <div class="modal-footer">
      <button class="btn-secondary" @click="onCancel">{{ $t("codTransferWaiting.cancel") }}</button>
      <button
        class="btn-primary"
        :disabled="!canSubmit || loading"
        :class="{ disabled: !canSubmit, loading }"
        @click="onSubmit"
      >
        <i class="fal fa-upload"></i> {{ $t("codTransferWaiting.upload_slip") }}
      </button>
    </div>
  </div>
</template>

<script>
import { confirmAndUploadSlip } from '@/views/Corporate/CodTransferWaiting/api';

export default {
  name: 'UploadSlipDialog',
  props: {
    item: { type: Object, required: true },
  },
  data() {
    return {
      loading: false,
      file: null,
      previewUrl: '',
      form: {
        bill_count: 0,
        transfer_date: '',
      },
      errors: { slip: '', bill_count: '', transfer_date: '' },
    };
  },
  computed: {
    canSubmit() {
      return !!(this.file && this.form.bill_count && this.form.transfer_date);
    },
  },
  watch: {
    file() { this.errors.slip = ''; },
    'form.bill_count'() { this.errors.bill_count = ''; },
    'form.transfer_date'() { this.errors.transfer_date = ''; },
  },
  created() {
    this.form.bill_count = this.item.bill_count || 0;
  },
  methods: {
    formatCurrency(v) { return (Number(v) || 0).toLocaleString('en-US'); },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      this.file = file;
      const reader = new FileReader();
      reader.onload = (ev) => { this.previewUrl = ev.target.result; };
      reader.readAsDataURL(file);
    },
    validate() {
      this.errors.slip = this.file ? '' : this.$t('codTransferWaiting.attach_slip_required');
      this.errors.bill_count = this.form.bill_count > 0 ? '' : this.$t('codTransferWaiting.enter_bill_count_required');
      this.errors.transfer_date = this.form.transfer_date ? '' : this.$t('codTransferWaiting.select_date_required');
      return !this.errors.slip && !this.errors.bill_count && !this.errors.transfer_date;
    },
    onCancel() {
      this.$emit('close');
    },
    onSubmit() {
      if (!this.validate()) return;
      this.loading = true;

      const payload = {
        slip_file: this.file,
        slip_preview_url: this.previewUrl,
        bill_count: this.form.bill_count,
        transfer_date: this.form.transfer_date,
      };

      confirmAndUploadSlip(this.$http, this.item.id, payload)
        .then((res) => {
          this.loading = false;
          this.$emit('success', res.data);
          this.$emit('close');
        })
        .catch(() => { this.loading = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
.upload-slip-modal {
  padding: 6px 10px 8px;
  width: 460px;
  max-width: 100%;
  text-align: left;
}

.modal-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
  color: #2c2f36;
  margin: 4px 0 16px;
}
.title-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #eef3ff;
  color: #195BFF;
  margin-right: 10px;
  font-size: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}
.summary-label { color: #8b8f99; }
.summary-value.strong { font-weight: 600; color: #2c2f36; }
.summary-value.amount { font-size: 15px; }

.form-group { margin-top: 14px; text-align: left; }
.label {
  font-weight: 500;
  color: #2c2f36;
  display: block;
  text-align: left;
  margin-bottom: 6px;
}
.text-danger { color: #ef4444; }
.error-text { color: #ef4444; font-size: 12px; margin-top: 4px; }

.input {
  width: 100%;
  height: 42px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  padding: 0 12px;
  font-size: 14px;
  font-family: $font;
  background: #fff;
  &:focus { outline: none; border-color: #195BFF; }
}

.input-with-suffix {
  position: relative;
  .suffix { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: #8b8f99; font-size: 13px; }
  .input { padding-right: 40px; }
}

.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #b5c2db;
  border-radius: 10px;
  background: #f8faff;
  padding: 20px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: #195BFF; background: #f0f5ff; }
  &.has-file { padding: 8px; }
}
.dropzone-empty { text-align: center; }
.dropzone-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #eef3ff;
  color: #195BFF;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 16px;
}
.dropzone-text { font-size: 14px; color: #4b5161; }
.dropzone-text .link { color: #195BFF; text-decoration: underline; }
.dropzone-sub { font-size: 12px; color: #8b8f99; margin-top: 4px; }
.preview-img { max-width: 100%; max-height: 180px; border-radius: 6px; }
.file-hidden { display: none; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}
.btn-secondary,
.btn-primary {
  height: 42px;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: $font;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
}
.btn-secondary {
  background: #fff;
  border: 1px solid #d9dde3;
  color: #4b5161;
  &:hover { background: #f5f7fb; }
}
.btn-primary {
  background: #195BFF;
  color: #fff;
  &:hover { background: #1450e6; }
  &.disabled, &:disabled { background: #cdd6eb; cursor: not-allowed; }
  &.loading { opacity: 0.7; cursor: progress; }
}
</style>
